import React from 'react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaStar, FaShoppingCart } from 'react-icons/fa';
import { useCart } from '../context/CartContext';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);

  const products = {
    1: {
      id: 1,
      name: "Natural Sandstone Pavers",
      price: 89.99,
      rating: 5,
      reviews: 127,
      image: "/images/1.jpg",
      description: "Our premium Australian sandstone pavers represent the finest quality natural stone available for outdoor landscaping. These pavers showcase the warm, earthy tones characteristic of Australian sandstone, with subtle variations in color and texture that create a naturally elegant appearance. Each paver is cut to consistent dimensions while preserving the organic character that makes natural stone so desirable.",
      features: [
        "Authentic Australian sandstone sourced from premium quarries",
        "Dimensions: 600mm x 400mm x 30mm for easy installation",
        "Natural slip-resistant surface perfect for outdoor areas",
        "Excellent durability with resistance to weathering and frost",
        "Warm beige and tan tones that complement any landscape design",
        "Low maintenance requirements with natural aging beauty",
        "Suitable for pathways, patios, pool surrounds, and driveways",
        "Each paver features unique natural patterns and textures"
      ],
      additionalInfo: "Sandstone pavers are an investment in timeless beauty. These natural stones have been formed over millions of years, creating a product that brings geological history into your garden. The porous nature of sandstone allows for excellent drainage, making these pavers ideal for Australian climates. They remain cool underfoot even in summer heat and develop a beautiful patina over time. Installation is straightforward, and these pavers can be laid in various patterns to create distinctive designs. Whether you are creating a garden pathway, outdoor entertaining area, or surrounding a water feature, these sandstone pavers provide the perfect foundation for stunning landscape design."
    },
    2: {
      id: 2,
      name: "River Stone Collection",
      price: 64.99,
      rating: 5,
      reviews: 94,
      image: "/images/2.jpg",
      description: "Our river stone collection features smooth, naturally tumbled stones that have been shaped by water over countless years. These beautiful stones display a range of colors from soft grays and whites to warm browns and tans, creating visual interest in any landscape application. The smooth, rounded surfaces make them comfortable to walk on and safe for areas where children play.",
      features: [
        "Naturally smoothed and rounded by river action over millennia",
        "Mixed sizes ranging from 50mm to 150mm diameter",
        "Color palette includes grays, whites, tans, and earth tones",
        "Perfect for dry creek beds and water feature surrounds",
        "Excellent for garden bed mulching and decorative borders",
        "Creates natural-looking landscapes with minimal maintenance",
        "Assists with drainage and erosion control in garden beds",
        "Safe, smooth surfaces ideal for barefoot areas and play spaces"
      ],
      additionalInfo: "River stones bring the calming essence of natural waterways to your garden. These stones are particularly versatile, serving both functional and aesthetic purposes. Use them to create dry creek beds that manage water flow during heavy rains, surround water features for an authentic natural appearance, or simply as decorative mulch in garden beds. The variety of sizes in each collection allows for creative arrangements, while the natural color variations ensure that each installation is unique. River stones also help suppress weeds, retain soil moisture, and regulate soil temperature, making them both beautiful and beneficial for your plants. Their timeless appeal means they will enhance your landscape for many years to come."
    },
    3: {
      id: 3,
      name: "Granite Garden Boulders",
      price: 149.99,
      rating: 5,
      reviews: 78,
      image: "/images/3.jpg",
      description: "These magnificent granite boulders serve as stunning focal points in landscape design, bringing dramatic natural beauty and structural interest to gardens of any size. Each boulder is a unique piece of natural art, featuring the characteristic speckled patterns and rich colors that make granite one of the most sought-after natural stones for landscaping.",
      features: [
        "Premium Australian granite with distinctive natural patterns",
        "Large feature stones ranging from 300mm to 800mm in size",
        "Exceptional durability and weather resistance for outdoor use",
        "Natural colors including grays, blacks, whites, and pink tones",
        "Perfect as garden focal points and landscape sculptures",
        "Creates dramatic elevation changes in flat landscape designs",
        "Ideal for rock gardens, Japanese-inspired gardens, and modern landscapes",
        "Each boulder is completely unique with individual character"
      ],
      additionalInfo: "Granite boulders are the cornerstone of impactful landscape design. These massive stones, formed deep within the earth under immense pressure and heat, bring a sense of permanence and natural majesty to outdoor spaces. Position them as standalone features to create visual anchors in large gardens, cluster them to form naturalistic rock gardens, or use them to define spaces and create elevation changes. Granite extraordinary hardness means these boulders will maintain their beauty indefinitely, requiring no maintenance while weathering gracefully. The interplay of light and shadow across their textured surfaces changes throughout the day, ensuring your landscape remains dynamic and interesting. Whether your design aesthetic is contemporary minimalist, traditional cottage, or Asian-inspired, granite boulders provide the natural gravitas that transforms ordinary gardens into extraordinary outdoor sanctuaries."
    },
    4: {
      id: 4,
      name: "Limestone Wall Cladding",
      price: 119.99,
      rating: 5,
      reviews: 112,
      image: "/images/4.jpg",
      description: "Our premium limestone wall cladding panels bring sophisticated natural beauty to exterior walls, garden features, and architectural elements. These carefully selected limestone pieces showcase the subtle elegance of natural stone, with soft cream and beige tones that complement both traditional and contemporary architectural styles.",
      features: [
        "High-quality natural limestone with consistent color tones",
        "Pre-cut panels for easier installation on vertical surfaces",
        "Dimensions designed for efficient coverage and minimal waste",
        "Natural variations create texture and visual depth",
        "Excellent for exterior walls, garden walls, and feature elements",
        "Weather-resistant and suitable for Australian outdoor conditions",
        "Soft cream and beige colors that brighten outdoor spaces",
        "Creates a luxurious appearance at an accessible price point"
      ],
      additionalInfo: "Limestone cladding transforms ordinary walls into architectural features worthy of admiration. This natural stone has been prized for centuries in prestigious buildings worldwide, and now you can bring that same elegance to your home exterior or garden walls. The natural porosity of limestone gives it excellent breathability, which helps prevent moisture issues behind walls while maintaining structural integrity. The soft, warm tones reflect light beautifully, creating an inviting atmosphere in outdoor living areas. Installation is straightforward with proper preparation, and the panels can be arranged in various patterns to achieve different aesthetic effects. Limestone ages gracefully, developing a subtle patina that actually enhances its character over time. Whether you are creating a privacy wall, enclosing an outdoor kitchen, or adding architectural interest to a plain exterior, limestone cladding delivers premium results that elevate the entire property."
    },
    5: {
      id: 5,
      name: "Slate Stepping Stones",
      price: 54.99,
      rating: 5,
      reviews: 156,
      image: "/images/5.jpg",
      description: "Our natural slate stepping stones offer an elegant and practical solution for creating pathways through gardens, lawns, and landscape beds. Each stone features the characteristic layered structure of slate, with rich colors ranging from deep charcoal to warm bronze tones, often within the same piece.",
      features: [
        "Authentic natural slate with distinctive layered appearance",
        "Irregular natural shapes that create organic pathway designs",
        "Average size 400mm to 600mm across for comfortable stepping",
        "Thickness of 20-40mm provides stability and durability",
        "Natural slip-resistant surface safe in wet conditions",
        "Color variations include grays, blacks, purples, and bronze tones",
        "Perfect for informal garden paths and lawn stepping stones",
        "Creates visual flow through landscape areas with natural elegance"
      ],
      additionalInfo: "Slate stepping stones invite exploration through your garden while protecting lawn and plant beds from foot traffic. The natural beauty of slate lies in its subtle color variations and the way light plays across its gently textured surface. Unlike uniform manufactured pavers, each slate stone is completely unique, with natural edges that blend seamlessly into garden settings. The inherent slip resistance of slate makes these stones safe for use around water features, in shaded areas, or anywhere moisture may be present. Laying slate stepping stones is an art form in itself, allowing you to create pathways that curve naturally through your landscape, following the contours of your garden. The gaps between stones can be filled with groundcover plants, fine gravel, or left as lawn, creating different aesthetic effects. These stepping stones are not just functional elements but become an integral part of your garden design, guiding visitors on a journey through your outdoor sanctuary while adding natural sophistication at every step."
    }
  };

  const product = products[id];

  if (!product) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <h2>Product not found</h2>
        <p>The product you are looking for does not exist.</p>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/checkout');
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => prev > 1 ? prev - 1 : 1);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <FaStar key={index} style={{ color: index < rating ? '#B87F5F' : '#E8E4DC', fontSize: '1.3rem' }} />
    ));
  };

  return (
    <>
      <Helmet>
        <title>{product.name} - Tanarela Premium Landscape Materials</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={`https://tanarela.com${product.image}`} />
        <link rel="canonical" href={`https://tanarela.com/product/${id}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": `https://tanarela.com${product.image}`,
            "offers": {
              "@type": "Offer",
              "price": product.price,
              "priceCurrency": "AUD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": product.rating,
              "reviewCount": product.reviews
            }
          })}
        </script>
      </Helmet>

      {showNotification && (
        <div className="notification">
          Product added to cart successfully!
        </div>
      )}

      <section className="product-detail-section">
        <div className="product-detail-container">
          <div className="product-detail-grid">
            <div className="product-detail-image-container">
              <img 
                src={product.image} 
                alt={product.name} 
                className="product-detail-image"
              />
            </div>

            <div className="product-detail-info">
              <h1>{product.name}</h1>
              
              <div className="product-detail-rating">
                <div className="stars">
                  {renderStars(product.rating)}
                </div>
                <span className="rating-text">({product.reviews} customer reviews)</span>
              </div>

              <div className="product-detail-price">AU${product.price}</div>

              <p className="product-detail-description">
                {product.description}
              </p>

              <div className="product-features">
                <h3>Key Features</h3>
                <ul>
                  {product.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>

              <div className="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button onClick={decrementQuantity} className="quantity-btn">-</button>
                  <span className="quantity-value">{quantity}</span>
                  <button onClick={incrementQuantity} className="quantity-btn">+</button>
                </div>
              </div>

              <div className="product-actions">
                <button onClick={handleAddToCart} className="btn-primary">
                  <FaShoppingCart /> Add to Cart
                </button>
                <button onClick={handleBuyNow} className="btn-secondary">
                  Buy Now
                </button>
              </div>
            </div>
          </div>

          <div className="additional-info">
            <h2>Detailed Product Information</h2>
            <p>{product.additionalInfo}</p>
          </div>
        </div>
      </section>
    </>
  );
}

export default ProductDetail;